import * as THREE from 'three';
import { std } from '../materials.ts';
import { M } from '../prims.ts';
import { Kit, place } from '../seasons/kit.ts';
import { begin, done, joint, type BaseOpts, type HeroRig, type Model } from './types.ts';

/*
 * Tormo (docs/sendas/artifice.md, chapter 5 and §10): Nadd's golem finished, the size of a tower. Dark iron with rows of
 * rivets, the shoulders' stone broken and set back with staples, the back's slab of stone, the heart's stone red through
 * a slit in the chest, the tube of dust in its cage on the back, a head with two eyes of blue glass and a clear light;
 * the knees and elbows show their gears. On the left shoulder's iron two marks scratched pale: Nadd's (a ring and its
 * stroke) and, once Brask cut it, the clan's (a hammer and a key). Seven plates where the nightmare glows (chest, the
 * knees, the shoulders, the back, the nape): dull iron on him whole; in his fight the layer lights them.
 *
 * Built bone by bone (the folk's rig: figure, legs and shins, body, arms and forearms, head), every bone's pieces merged
 * into one mesh of vertex colours and one of its lights: a handful of draw calls for something this big. `buildTormo`
 * gives the parts to the fight's layer (engine/tormo.ts: the plates and stones apart, to light and drop); `tormoFig` is
 * the cast's miniature (`senda:tormo`). Also the ash cub (`ashCub`): the cubs of the shell and the plates.
 *
 * Colours: cold iron and slate stone, bronze gone dark (never gold), the heart and the dust the dragon's red, the eyes a
 * cold blue (never warm white).
 */

// (Lighter than black iron and with bright worn edges: at the game's camera and under the yard's sky the first Tormo
// read as a black box. The edges and rivets are what make him read as plates of iron, not one block.)
const IRON = '#3a4048';
const IRON_D = '#23272d';
const IRON_L = '#56606b';
const EDGE = '#9aa4b0';
const RIVET = '#a9b2bc';
const RUST = '#5c3428';
const RUST_D = '#40261f';
const STONE = ['#7d838b', '#71777f', '#8a9098'];
const STONE_L = '#a2a8af';
const STONE_D = '#565c64';
const BRONZE = '#5c5246';
const SCRATCH = '#c9d0d8';
const ROPE = '#6e6151';
const RED = '#ff4f3a';
const EYE = '#bfe6ff';

export type TormoBone = 'figure' | 'body' | 'head' | 'armL' | 'armR' | 'foreL' | 'foreR' | 'legL' | 'legR' | 'shinL' | 'shinR';

/** Where each plate sits (absolute model coordinates, before the model's scale), on which bone, and the way it faces (yaw). */
export const TORMO_PLATES: readonly { bone: TormoBone; at: [number, number, number]; yaw: number; size: number }[] = [
  { bone: 'body', at: [0, 2.3, 0.6], yaw: 0, size: 0.5 },
  { bone: 'shinL', at: [-0.44, 0.56, 0.36], yaw: -0.35, size: 0.36 },
  { bone: 'shinR', at: [0.44, 0.56, 0.36], yaw: 0.35, size: 0.36 },
  { bone: 'armL', at: [-1.34, 2.14, 0.02], yaw: -Math.PI / 2, size: 0.42 },
  { bone: 'armR', at: [1.34, 2.14, 0.02], yaw: Math.PI / 2, size: 0.42 },
  { bone: 'body', at: [0, 2.12, -0.56], yaw: Math.PI, size: 0.52 },
  { bone: 'head', at: [0, 3.06, -0.31], yaw: Math.PI, size: 0.3 },
];

/** The scale inside the model (as tall as a tower next to the people). */
export const TORMO_SCALE = 1.75;
/** His height in model units at that scale. */
export const TORMO_TALL = 3.35 * TORMO_SCALE;

export interface TormoOpts {
  /** The eyes: blue glass with a clear light (himself), or black (what got in). */
  eyes?: 'blue' | 'dark';
  /** The dust in his tube: the old (its cracks glow a stronger red) or Grillo's (the tube's light, steady here). */
  dust?: 'old' | 'grillo';
  /** The clan's mark cut beside Nadd's. */
  clan?: boolean;
  /** The plates and stones apart (the fight's layer lights the one and drops the other); else merged in. */
  apart?: boolean;
  /** Tied in the yard: the quarry's cable twice round his chest and his arms, its loose end on the ground. */
  tied?: boolean;
  /**
   * Each piece its own mesh in a plain material of its colour (the library's baking merges those by finish; it drops a
   * merged mesh's vertex colours): the cast's miniature. Else each bone one merged mesh (the fight's, the trophy's).
   */
  plain?: boolean;
}

export interface TormoBuild {
  root: THREE.Group;
  g: THREE.Group;
  rig: HeroRig;
  pivots: Record<TormoBone, THREE.Group>;
  /** Apart only: each plate (its dark face, its rim and its core that glows when lit), each stone (left shoulder, right shoulder, back). */
  plates: { group: THREE.Group; rim: THREE.MeshBasicMaterial; core: THREE.MeshBasicMaterial }[];
  stones: THREE.Group[];
  /** The lights' materials: the heart, the tube, the eyes, the cracks in the shoulders' stone. */
  heart: THREE.MeshBasicMaterial;
  tube: THREE.MeshBasicMaterial;
  eyes: THREE.MeshBasicMaterial;
  cracks: THREE.MeshBasicMaterial;
  dispose(): void;
}

const BOX = new THREE.BoxGeometry(1, 1, 1);
const CYL = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
const SPH = new THREE.SphereGeometry(0.5, 10, 8);
const TOR = new THREE.TorusGeometry(0.5, 0.12, 5, 14);
const DODE = new THREE.DodecahedronGeometry(0.5, 0);
const GEAR = (() => {
  // A gear's outline: a disc with teeth, flat (merged in a bone's mesh).
  const s = new THREE.Shape();
  const n = 12;
  for (let i = 0; i < n * 2; i++) {
    const a = (i / (n * 2)) * Math.PI * 2;
    const r = i % 2 === 0 ? 0.5 : 0.4;
    const a2 = a + Math.PI / (n * 2);
    if (i === 0) s.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else s.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    s.lineTo(Math.cos(a2) * r, Math.sin(a2) * r);
  }
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.12, bevelEnabled: false });
  g.translate(0, 0, -0.06);
  return g;
})();

const b = (x: number, y: number, z: number, sx: number, sy: number, sz: number, yaw = 0, tx = 0, tz = 0): THREE.Matrix4 => place(x, y, z, yaw, new THREE.Vector3(sx, sy, sz), tx, tz);

/** A small deterministic wobble (no randomness in a model: the same Tormo every time). */
const jit = (i: number): number => Math.sin(i * 12.9898 + 4.1) * 0.5;

/** A plate's radial glow: hot in the middle, the dragon's red toward the rim (never white: over red it burns cream). */
let coreTexture: THREE.Texture | null = null;
const coreTex = (): THREE.Texture | null => {
  if (coreTexture) return coreTexture;
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return null;
  c.width = c.height = 64;
  const g = c.getContext('2d');
  if (!g) return null;
  const gr = g.createRadialGradient(32, 32, 2, 32, 32, 34);
  gr.addColorStop(0, '#ffb39a');
  gr.addColorStop(0.35, '#ff6a48');
  gr.addColorStop(0.75, '#c81e0c');
  gr.addColorStop(1, '#3a0a05');
  g.fillStyle = gr;
  g.fillRect(0, 0, 64, 64);
  // Cracks from the middle out: what got in, under the iron.
  g.strokeStyle = 'rgba(40,6,3,0.75)';
  g.lineWidth = 2;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.4;
    g.beginPath();
    g.moveTo(32 + Math.cos(a) * 6, 32 + Math.sin(a) * 6);
    g.lineTo(32 + Math.cos(a + 0.3) * 18, 32 + Math.sin(a + 0.3) * 18);
    g.lineTo(32 + Math.cos(a) * 31, 32 + Math.sin(a) * 31);
    g.stroke();
  }
  coreTexture = new THREE.CanvasTexture(c);
  return coreTexture;
};

/** Builds Tormo bone by bone (see the file's head). */
export const buildTormo = (o: TormoOpts = {}, base?: BaseOpts): TormoBuild => {
  const c = begin(base, 1.4, 905);
  const g = c.g;
  g.scale.setScalar(TORMO_SCALE);
  const fig = joint(g, new THREE.Vector3(0, 0, 0), 'figure');
  const legL = joint(fig.abs, new THREE.Vector3(-0.43, 1.36, 0), 'legL');
  const legR = joint(fig.abs, new THREE.Vector3(0.43, 1.36, 0), 'legR');
  const shinL = joint(legL.abs, new THREE.Vector3(-0.44, 0.74, 0.04), 'shinL');
  const shinR = joint(legR.abs, new THREE.Vector3(0.44, 0.74, 0.04), 'shinR');
  const body = joint(fig.abs, new THREE.Vector3(0, 1.4, 0), 'body');
  const armL = joint(body.abs, new THREE.Vector3(-1.04, 2.52, 0), 'armL');
  const armR = joint(body.abs, new THREE.Vector3(1.04, 2.52, 0), 'armR');
  const foreL = joint(armL.abs, new THREE.Vector3(-1.1, 1.7, 0.06), 'foreL');
  const foreR = joint(armR.abs, new THREE.Vector3(1.1, 1.7, 0.06), 'foreR');
  const head = joint(body.abs, new THREE.Vector3(0, 2.78, 0), 'head');
  // (A head a size up: at the game's high camera the shoulders' stones hid it and he read as a slab.)
  head.p.scale.setScalar(1.25);
  head.p.position.y += 0.04;
  const abs: Record<TormoBone, THREE.Group> = { figure: fig.abs, body: body.abs, head: head.abs, armL: armL.abs, armR: armR.abs, foreL: foreL.abs, foreR: foreR.abs, legL: legL.abs, legR: legR.abs, shinL: shinL.abs, shinR: shinR.abs };
  const pivots: Record<TormoBone, THREE.Group> = { figure: fig.p, body: body.p, head: head.p, armL: armL.p, armR: armR.p, foreL: foreL.p, foreR: foreR.p, legL: legL.p, legR: legR.p, shinL: shinL.p, shinR: shinR.p };
  const mats: THREE.Material[] = [];
  const geos: THREE.BufferGeometry[] = [];
  const byColor = new Map<string, THREE.MeshStandardMaterial>();
  const glowMat = (color: string): THREE.MeshBasicMaterial => {
    const m = new THREE.MeshBasicMaterial({ color });
    mats.push(m);
    return m;
  };
  const heart = glowMat(RED);
  const tube = glowMat(o.dust === 'old' ? '#a81c0c' : '#d8331e');
  const eyes = glowMat(o.eyes === 'dark' ? '#0c0d10' : EYE);
  // The shoulders' cracks: a dull ember with Grillo's dust, the dragon's red with the old (§10).
  const cracks = glowMat(o.dust === 'old' ? RED : '#9a2614');
  /** A sink for one group's pieces: merged (one mesh of vertex colours) or plain (a mesh a piece, the cast's). */
  type Sink = Pick<Kit, 'add'>;
  const sinks = new Map<THREE.Object3D, { k: Kit; glow: Kit }>();
  const plainSink = (target: THREE.Object3D, glow: boolean): Sink => ({
    add: (geo: THREE.BufferGeometry, color: THREE.ColorRepresentation, m: THREE.Matrix4, local?: THREE.Matrix4): void => {
      let mat: THREE.Material;
      if (glow) mat = cracks;
      else {
        const key = String(color);
        let sm = byColor.get(key);
        if (!sm) {
          sm = std(key, { roughness: 0.62, metalness: 0.35, flatShading: true });
          byColor.set(key, sm);
          mats.push(sm);
        }
        mat = sm;
      }
      const mesh = new THREE.Mesh(geo, mat);
      mesh.applyMatrix4(local ? new THREE.Matrix4().multiplyMatrices(m, local) : m);
      target.add(mesh);
    },
  });
  const sinkOf = (target: THREE.Object3D): { k: Kit; glow: Kit } => {
    let s0 = sinks.get(target);
    if (!s0) sinks.set(target, (s0 = { k: new Kit(), glow: new Kit() }));
    return s0;
  };
  const kitOf = (target: THREE.Object3D): Sink => (o.plain ? plainSink(target, false) : sinkOf(target).k);
  const glowOf = (target: THREE.Object3D): Sink => (o.plain ? plainSink(target, true) : sinkOf(target).glow);
  const kit = (bone: TormoBone): Sink => kitOf(abs[bone]);
  let ri = 0;
  const rivetRow = (k: Sink, from: THREE.Vector3, to: THREE.Vector3, n: number, r = 0.05, rust = false): void => {
    for (let i = 0; i < n; i++) {
      const p = from.clone().lerp(to, n === 1 ? 0.5 : i / (n - 1));
      k.add(SPH, RIVET, b(p.x, p.y, p.z, r, r, r * 0.7));
      // Now and then a streak of rust down from it (the wet of fifty-five years in a cave).
      if (rust && jit(ri++) > 0.15) k.add(BOX, RUST, b(p.x, p.y - 0.09, p.z - 0.004, 0.022, 0.15, 0.01));
    }
  };
  /**
   * A plate of iron: its face, a bright worn edge along the top (where the light catches it) and a dark seam under it.
   * `nz` the way its face looks (+1 front, -1 back, 0 sideways by `yaw`).
   */
  const plate = (k: Sink, x: number, y: number, z: number, w: number, h: number, d: number, col = IRON, yaw = 0, tx = 0, tz = 0): void => {
    k.add(BOX, col, b(x, y, z, w, h, d, yaw, tx, tz));
    k.add(BOX, EDGE, place(x, y, z, yaw, new THREE.Vector3(w * 1.01, 0.035, d * 1.02), tx, tz), b(0, h / 2 / 0.035, 0, 1, 1, 1));
    k.add(BOX, IRON_D, place(x, y, z, yaw, new THREE.Vector3(w * 1.015, 0.03, d * 1.03), tx, tz), b(0, -h / 2 / 0.03, 0, 1, 1, 1));
  };
  const scratch = (k: Sink, x: number, y: number, z: number, len: number, ang: number, yaw = 0): void => {
    k.add(BOX, SCRATCH, place(x, y, z, yaw, new THREE.Vector3(0.016, len, 0.008), 0, ang));
  };

  // ---------- legs: the thigh's plates, the knee's cop and its gears in sight, the greave, a stepped iron boot ----------
  for (const k of [-1, 1]) {
    const leg: TormoBone = k < 0 ? 'legL' : 'legR';
    const shin: TormoBone = k < 0 ? 'shinL' : 'shinR';
    const lk = kit(leg);
    const sk = kit(shin);
    lk.add(BOX, IRON_D, b(k * 0.43, 1.06, 0.0, 0.46, 0.66, 0.5));
    plate(lk, k * 0.43, 1.18, 0.05, 0.56, 0.34, 0.56, IRON);
    plate(lk, k * 0.43, 0.9, 0.06, 0.52, 0.24, 0.54, IRON_L);
    rivetRow(lk, new THREE.Vector3(k * 0.24, 1.3, 0.34), new THREE.Vector3(k * 0.62, 1.3, 0.34), 4, 0.05, true);
    lk.add(GEAR, BRONZE, b(k * 0.7, 0.78, 0.04, 0.48, 0.48, 0.8, k * Math.PI / 2));
    lk.add(GEAR, BRONZE, b(k * 0.72, 1.0, -0.2, 0.3, 0.3, 0.8, k * Math.PI / 2));
    lk.add(CYL, IRON_D, b(k * 0.68, 0.78, 0.04, 0.12, 0.1, 0.12, 0, 0, Math.PI / 2));
    // The knee's cop: a heavy shell over the joint, tilted to the front.
    plate(sk, k * 0.44, 0.8, 0.3, 0.42, 0.26, 0.16, IRON_L, 0, -0.35);
    sk.add(SPH, RIVET, b(k * 0.44, 0.8, 0.4, 0.07, 0.07, 0.05));
    // The greave, wider at the ankle, and its band.
    sk.add(BOX, IRON, b(k * 0.44, 0.5, 0.06, 0.5, 0.5, 0.54));
    plate(sk, k * 0.44, 0.56, 0.3, 0.42, 0.34, 0.06, IRON_L);
    rivetRow(sk, new THREE.Vector3(k * 0.26, 0.38, 0.34), new THREE.Vector3(k * 0.62, 0.38, 0.34), 3, 0.045);
    scratch(sk, k * 0.36, 0.56, 0.34, 0.22, 0.5 * k);
    // The boot: a broad sole, the instep in three plates stepping down to the toe, a heel's block.
    sk.add(BOX, IRON_D, b(k * 0.46, 0.08, 0.16, 0.66, 0.16, 0.96));
    plate(sk, k * 0.46, 0.25, 0.08, 0.58, 0.2, 0.56, IRON);
    plate(sk, k * 0.46, 0.2, 0.42, 0.56, 0.12, 0.2, IRON_L);
    plate(sk, k * 0.46, 0.15, 0.58, 0.52, 0.08, 0.16, IRON);
    sk.add(BOX, RUST_D, b(k * 0.46, 0.02, 0.16, 0.68, 0.03, 0.98));
  }

  // ---------- the body: hips, the belly's lames, a broad chest of two great plates, the collar, the heart's slit ----------
  const bk = kit('body');
  bk.add(BOX, IRON_D, b(0, 1.44, 0, 1.24, 0.36, 0.78));
  for (const k of [-1, 1]) {
    bk.add(GEAR, BRONZE, b(k * 0.66, 1.46, 0.02, 0.42, 0.42, 0.8, k * Math.PI / 2));
    bk.add(CYL, IRON_D, b(k * 0.67, 1.46, 0.02, 0.12, 0.08, 0.12, 0, 0, Math.PI / 2));
  }
  // The belly's lames, each a little wider going up (a skirt of iron bands).
  [1.62, 1.74, 1.86].forEach((y, i) => plate(bk, 0, y, 0.02, 1.3 + i * 0.08, 0.13, 0.84 + i * 0.04, i % 2 ? IRON_L : IRON));
  // The chest: the core, wider at the top; the two great plates over it with a seam down the middle (the heart's slit).
  bk.add(BOX, IRON_D, b(0, 2.24, 0.0, 1.7, 0.86, 1.0));
  plate(bk, 0, 2.62, 0.0, 1.88, 0.2, 1.08, IRON);
  for (const k of [-1, 1]) {
    plate(bk, k * 0.47, 2.28, 0.5, 0.74, 0.66, 0.1, IRON, -k * 0.14);
    bk.add(BOX, IRON_L, b(k * 0.84, 2.24, 0.42, 0.12, 0.8, 0.14, -k * 0.5));
    rivetRow(bk, new THREE.Vector3(k * 0.8, 1.98, 0.48), new THREE.Vector3(k * 0.8, 2.56, 0.48), 5, 0.05, true);
    rivetRow(bk, new THREE.Vector3(k * 0.2, 2.56, 0.58), new THREE.Vector3(k * 0.72, 2.56, 0.5), 4, 0.045);
    rivetRow(bk, new THREE.Vector3(k * 0.2, 1.99, 0.58), new THREE.Vector3(k * 0.72, 1.99, 0.5), 4, 0.045, true);
  }
  scratch(bk, -0.42, 2.3, 0.565, 0.32, 0.7);
  scratch(bk, -0.5, 2.22, 0.565, 0.2, 0.9);
  scratch(bk, 0.55, 2.36, 0.56, 0.26, -0.6);
  bk.add(BOX, RUST, b(0.3, 2.05, 0.56, 0.12, 0.08, 0.01));
  // The collar: a ring of plate round the neck's foot.
  plate(bk, 0, 2.76, 0.02, 0.96, 0.14, 0.7, IRON_L);
  // The heart's slit: its iron lips, the stone red behind.
  bk.add(BOX, IRON_D, b(-0.13, 2.18, 0.57, 0.07, 0.68, 0.06));
  bk.add(BOX, IRON_D, b(0.13, 2.18, 0.57, 0.07, 0.68, 0.06));
  const heartMesh = M(BOX, heart, body.abs);
  heartMesh.position.set(0, 2.18, 0.55);
  heartMesh.scale.set(0.19, 0.58, 0.04);
  // The back: the tube of dust in its cage, high on the right, and its two pipes into the shoulders.
  const tubeMesh = M(CYL, tube, body.abs);
  tubeMesh.position.set(0.5, 2.3, -0.88);
  tubeMesh.scale.set(0.24, 0.72, 0.24);
  for (const y of [1.96, 2.3, 2.64]) bk.add(TOR, IRON_D, b(0.5, y, -0.88, 0.32, 0.32, 0.32, 0, Math.PI / 2));
  for (const a of [0, 2.1, 4.2]) bk.add(BOX, IRON_D, b(0.5 + Math.cos(a) * 0.15, 2.3, -0.88 + Math.sin(a) * 0.15, 0.04, 0.82, 0.04));
  bk.add(CYL, IRON_D, b(0.5, 2.72, -0.88, 0.2, 0.08, 0.2));
  bk.add(CYL, IRON_L, b(0.36, 2.74, -0.6, 0.07, 0.6, 0.07, 0, 1.2));
  bk.add(CYL, IRON_L, b(0.64, 1.92, -0.66, 0.06, 0.5, 0.06, 0, 0.9));
  rivetRow(bk, new THREE.Vector3(-0.6, 2.5, -0.52), new THREE.Vector3(0.2, 2.5, -0.52), 5, 0.045, true);

  // ---------- the stones: the shoulders' blocks (broken and set back, stapled, their cracks lit) and the back's slab ----------
  const stones: THREE.Group[] = [];
  const stoneAt: [number, number, number, number, number, number][] = [
    [-1.12, 2.6, 0, 0.84, 0.56, 0.9],
    [1.12, 2.6, 0, 0.84, 0.56, 0.9],
    [-0.08, 2.12, -0.66, 1.3, 0.9, 0.28],
  ];
  stoneAt.forEach(([x, y, z, sx, sy, sz], i) => {
    const grp = o.apart ? new THREE.Group() : body.abs;
    if (o.apart) {
      body.abs.add(grp);
      stones.push(grp);
    }
    const sk = kitOf(grp);
    const gk = glowOf(grp);
    const s0 = STONE[i % 3] as string;
    if (i < 2) {
      const k = i === 0 ? -1 : 1;
      // Broken in two across and set back a little askew: the outer half lower, a chip gone from its corner.
      sk.add(BOX, s0, b(x - k * 0.2, y + 0.04, z, sx * 0.55, sy, sz, 0, 0.03, -k * 0.05));
      sk.add(BOX, STONE[(i + 1) % 3] as string, b(x + k * 0.24, y - 0.02, z + 0.02, sx * 0.44, sy * 0.92, sz * 0.96, 0.05 * k, -0.04, k * 0.09));
      sk.add(BOX, STONE_L, b(x - k * 0.2, y + sy / 2 + 0.05, z, sx * 0.53, 0.04, sz * 0.97, 0, 0.03, -k * 0.05));
      sk.add(BOX, STONE_L, b(x + k * 0.24, y + sy * 0.46 - 0.02, z + 0.02, sx * 0.42, 0.04, sz * 0.93, 0.05 * k, -0.04, k * 0.09));
      sk.add(BOX, STONE_D, b(x, y - sy / 2 + 0.02, z, sx * 1.02, 0.06, sz * 1.02));
      sk.add(DODE, STONE[(i + 2) % 3] as string, b(x + k * 0.36, y + 0.24, z + 0.4, 0.2, 0.16, 0.18, 0.7));
      // The cracks: the break between the halves and two that run from it, on top and on the front (lit from within).
      gk.add(BOX, '#ffffff', b(x + k * 0.02, y + 0.02, z + sz / 2 + 0.01, 0.04, sy * 0.96, 0.03, 0, 0, k * 0.09));
      gk.add(BOX, '#ffffff', b(x + k * 0.02, y + sy / 2 + 0.06, z, 0.04, 0.03, sz * 0.96));
      gk.add(BOX, '#ffffff', b(x - k * 0.22, y - 0.08, z + sz / 2 + 0.01, 0.03, sy * 0.5, 0.03, 0, 0, k * 0.6));
      gk.add(BOX, '#ffffff', b(x + k * 0.3, y + 0.1, z + sz / 2 + 0.03, 0.025, sy * 0.38, 0.03, 0, 0, -k * 0.5));
      gk.add(BOX, '#ffffff', b(x - k * 0.1, y + sy / 2 + 0.065, z + 0.2, 0.025, 0.02, sz * 0.4, 0.5));
      // The staples across the break, iron, two on top and one in front.
      for (const dz of [-0.3, 0.3]) sk.add(BOX, IRON_D, b(x + k * 0.02, y + sy / 2 + 0.08, z + dz, 0.34, 0.06, 0.08));
      sk.add(BOX, IRON_D, b(x + k * 0.02, y + 0.12, z + sz / 2 + 0.03, 0.32, 0.07, 0.05));
      for (const dx of [-0.13, 0.13]) sk.add(SPH, RIVET, b(x + k * 0.02 + dx, y + sy / 2 + 0.1, z - 0.3, 0.05, 0.05, 0.05));
    } else {
      sk.add(BOX, s0, b(x, y, z, sx, sy, sz, 0, 0.04, -0.03));
      sk.add(BOX, STONE_L, b(x, y + sy / 2 + 0.02, z, sx * 0.98, 0.04, sz * 0.96));
      gk.add(BOX, '#ffffff', b(x - 0.2, y, z - sz / 2 - 0.01, 0.035, sy * 0.8, 0.03, 0, 0, 0.25));
      gk.add(BOX, '#ffffff', b(x + 0.18, y - 0.18, z - sz / 2 - 0.01, 0.03, sy * 0.4, 0.03, 0, 0, -0.7));
      for (const dx of [-0.48, 0, 0.48]) sk.add(BOX, IRON_D, b(x + dx, y + 0.3, z - 0.02, 0.09, 0.09, sz + 0.12));
    }
  });

  // ---------- the marks on the left shoulder's iron (the upper arm's front): Nadd's, and the clan's beside it ----------
  const ak = kit('armL');
  ak.add(TOR, SCRATCH, b(-1.08, 2.3, 0.29, 0.15, 0.15, 0.1));
  ak.add(BOX, SCRATCH, b(-1.08, 2.2, 0.29, 0.03, 0.16, 0.02));
  if (o.clan) {
    // A hammer (its head, its haft) and a key, cut a little rougher.
    ak.add(BOX, SCRATCH, b(-1.14, 2.0, 0.29, 0.16, 0.05, 0.02));
    ak.add(BOX, SCRATCH, b(-1.14, 1.92, 0.29, 0.03, 0.16, 0.02));
    ak.add(TOR, SCRATCH, b(-0.98, 2.04, 0.29, 0.07, 0.07, 0.08));
    ak.add(BOX, SCRATCH, b(-0.98, 1.94, 0.29, 0.025, 0.13, 0.02));
  }

  // ---------- arms: the upper arm in bands, the elbow's gears, a vambrace wider at the wrist, a fist as big as a cask ----------
  for (const k of [-1, 1]) {
    const arm: TormoBone = k < 0 ? 'armL' : 'armR';
    const fore: TormoBone = k < 0 ? 'foreL' : 'foreR';
    const uk = kit(arm);
    const fk = kit(fore);
    uk.add(BOX, IRON_D, b(k * 1.08, 2.1, 0.02, 0.42, 0.9, 0.46));
    plate(uk, k * 1.08, 2.22, 0.02, 0.5, 0.36, 0.54, IRON);
    plate(uk, k * 1.08, 1.92, 0.03, 0.48, 0.2, 0.52, IRON_L);
    rivetRow(uk, new THREE.Vector3(k * 1.33, 2.32, -0.16), new THREE.Vector3(k * 1.33, 2.32, 0.2), 3, 0.045, true);
    uk.add(GEAR, BRONZE, b(k * 1.36, 1.74, 0.06, 0.44, 0.44, 0.8, k * Math.PI / 2));
    uk.add(GEAR, BRONZE, b(k * 1.38, 1.92, -0.14, 0.26, 0.26, 0.8, k * Math.PI / 2));
    uk.add(CYL, IRON_D, b(k * 1.36, 1.74, 0.06, 0.12, 0.1, 0.12, 0, 0, Math.PI / 2));
    fk.add(BOX, IRON_D, b(k * 1.12, 1.36, 0.12, 0.44, 0.7, 0.48));
    plate(fk, k * 1.12, 1.44, 0.12, 0.5, 0.34, 0.54, IRON);
    plate(fk, k * 1.13, 1.12, 0.13, 0.58, 0.22, 0.62, IRON_L);
    rivetRow(fk, new THREE.Vector3(k * 1.12, 1.58, 0.4), new THREE.Vector3(k * 1.12, 1.26, 0.4), 3, 0.045, true);
    scratch(fk, k * 1.2, 1.42, 0.4, 0.2, 0.6 * k);
    // The fist: the back of the hand, four knuckles and a thumb, the fingers' line dark under them.
    fk.add(BOX, IRON, b(k * 1.14, 0.82, 0.18, 0.6, 0.42, 0.62));
    fk.add(BOX, IRON_D, b(k * 1.14, 0.72, 0.42, 0.56, 0.06, 0.2));
    for (let i = 0; i < 4; i++) {
      const fx = k * 1.14 + (i - 1.5) * 0.14;
      fk.add(BOX, IRON_L, b(fx, 0.9, 0.48, 0.12, 0.16, 0.14));
      fk.add(BOX, EDGE, b(fx, 0.985, 0.48, 0.12, 0.02, 0.14));
    }
    fk.add(BOX, IRON_L, b(k * 0.84, 0.82, 0.32, 0.12, 0.26, 0.2, 0, 0, k * 0.3));
    fk.add(BOX, RUST_D, b(k * 1.14, 0.6, 0.18, 0.58, 0.03, 0.6));
  }

  // ---------- the head: a neck, a helm's block with a crest, a heavy brow, cheeks and a grille, the eyes in slots ----------
  const hk = kit('head');
  hk.add(CYL, IRON_D, b(0, 2.86, 0, 0.4, 0.2, 0.4));
  hk.add(BOX, IRON, b(0, 3.1, 0.02, 0.66, 0.52, 0.6));
  hk.add(BOX, IRON_L, b(0, 3.38, 0.02, 0.6, 0.06, 0.56));
  hk.add(BOX, IRON_L, b(0, 3.42, 0.02, 0.12, 0.12, 0.64));
  hk.add(BOX, EDGE, b(0, 3.485, 0.02, 0.12, 0.02, 0.64));
  rivetRow(hk, new THREE.Vector3(-0.2, 3.41, -0.28), new THREE.Vector3(-0.2, 3.41, 0.3), 4, 0.035);
  rivetRow(hk, new THREE.Vector3(0.2, 3.41, -0.28), new THREE.Vector3(0.2, 3.41, 0.3), 4, 0.035);
  // The brow, jutting over the eyes, its edge worn bright.
  hk.add(BOX, IRON_D, b(0, 3.24, 0.33, 0.74, 0.12, 0.16, 0, 0.25));
  hk.add(BOX, EDGE, b(0, 3.3, 0.38, 0.74, 0.025, 0.08, 0, 0.25));
  // The sockets (deep, their rims pale so even black eyes read), the eyes as slots of glass.
  for (const k of [-1, 1]) {
    hk.add(BOX, '#0f1114', b(k * 0.16, 3.13, 0.33, 0.2, 0.11, 0.04));
    hk.add(BOX, IRON_L, b(k * 0.16, 3.075, 0.335, 0.22, 0.02, 0.04));
    const e = M(BOX, eyes, head.abs);
    e.position.set(k * 0.16, 3.13, 0.345);
    e.scale.set(0.16, 0.07, 0.03);
  }
  // The cheeks angled back, the jaw and its grille.
  for (const k of [-1, 1]) plate(hk, k * 0.3, 3.0, 0.22, 0.1, 0.3, 0.26, IRON_L, k * 0.45);
  plate(hk, 0, 2.94, 0.25, 0.5, 0.18, 0.2, IRON_L);
  for (let i = 0; i < 4; i++) hk.add(BOX, '#121417', b((i - 1.5) * 0.09, 2.95, 0.355, 0.035, 0.12, 0.02));
  scratch(hk, 0.22, 3.18, 0.325, 0.14, -0.4);

  // ---------- tied in the yard: the quarry's cable round his chest twice, round his arms, its end on the ground ----------
  if (o.tied) {
    for (const [y, t] of [
      [2.02, 0.08],
      [2.42, -0.1],
    ] as const) {
      bk.add(TOR, ROPE, b(0, y, -0.02, 1.98, 1.2, 0.42, 0, Math.PI / 2 + t));
    }
    for (const k of [-1, 1]) kit(k < 0 ? 'foreL' : 'foreR').add(TOR, ROPE, b(k * 1.12, 1.3, 0.12, 0.58, 0.6, 0.36, 0, Math.PI / 2));
    kit('figure').add(CYL, ROPE, b(0.9, 0.04, 0.9, 0.05, 1.6, 0.05, 0.6, 0, Math.PI / 2));
  }

  // ---------- the plates: apart for the fight; else dull iron merged into their bones ----------
  const plates: { group: THREE.Group; rim: THREE.MeshBasicMaterial; core: THREE.MeshBasicMaterial }[] = [];
  const ct = coreTex();
  for (const P of TORMO_PLATES) {
    const [x, y, z] = P.at;
    const s = P.size;
    if (!o.apart) {
      kit(P.bone).add(BOX, '#2e3339', b(x, y, z, s, s, 0.06, P.yaw));
      continue;
    }
    const grp = new THREE.Group();
    grp.position.set(x, y, z);
    grp.rotation.y = P.yaw;
    const face = new Kit();
    face.add(BOX, '#1a1d21', b(0, 0, 0, s, s, 0.07));
    const faceMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.45, metalness: 0.5 });
    mats.push(faceMat);
    const fm = face.build(faceMat, 'tormo-plate');
    if (fm) {
      geos.push(fm.geometry);
      grp.add(fm);
    }
    // Its core: a face that glows from the middle out when the nightmare is there (dark otherwise).
    const core = new THREE.MeshBasicMaterial({ color: '#1a0d0a', ...(ct ? { map: ct } : {}) });
    mats.push(core);
    const cg = new THREE.PlaneGeometry(s * 0.86, s * 0.86);
    geos.push(cg);
    const cm = new THREE.Mesh(cg, core);
    cm.position.z = 0.04;
    cm.name = 'tormo-plate-core';
    grp.add(cm);
    const rk = new Kit();
    const t = s * 0.12;
    rk.add(BOX, '#ffffff', b(0, s / 2, 0.02, s + t, t, 0.09));
    rk.add(BOX, '#ffffff', b(0, -s / 2, 0.02, s + t, t, 0.09));
    rk.add(BOX, '#ffffff', b(s / 2, 0, 0.02, t, s, 0.09));
    rk.add(BOX, '#ffffff', b(-s / 2, 0, 0.02, t, s, 0.09));
    const rim = new THREE.MeshBasicMaterial({ color: RED, vertexColors: true });
    mats.push(rim);
    const rm = rk.build(rim, 'tormo-plate-rim');
    if (rm) {
      geos.push(rm.geometry);
      grp.add(rm);
    }
    abs[P.bone].add(grp);
    plates.push({ group: grp, rim, core });
  }

  // ---------- each group's merged meshes: its iron and stone, and its cracks (lit) ----------
  const iron = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.58, metalness: 0.38, flatShading: true });
  mats.push(iron);
  let n = 0;
  for (const [target, sk] of sinks) {
    const m = sk.k.build(iron, `tormo-${n++}`);
    if (m) {
      m.castShadow = true;
      geos.push(m.geometry);
      target.add(m);
    }
    const gm = sk.glow.build(cracks, `tormo-cracks-${n}`);
    if (gm) {
      geos.push(gm.geometry);
      target.add(gm);
    }
  }
  const rig: HeroRig = {
    figure: fig.p,
    body: body.p,
    head: head.p,
    armL: armL.p,
    armR: armR.p,
    legL: legL.p,
    legR: legR.p,
    foreL: foreL.p,
    foreR: foreR.p,
    shinL: shinL.p,
    shinR: shinR.p,
    weapon: new THREE.Group(),
    offhand: new THREE.Group(),
  };
  foreR.abs.add(rig.weapon);
  foreL.abs.add(rig.offhand);
  return {
    root: c.root,
    g,
    rig,
    pivots,
    plates,
    stones,
    heart,
    tube,
    eyes,
    cracks,
    dispose: () => {
      for (const m of mats) m.dispose();
      for (const x of geos) x.dispose();
    },
  };
};

/** The kneel (`k` 0 standing to 1 knelt): the right knee down, the left foot planted, a little bowed. The fight's and the cast's. */
export const TORMO_KNEEL: Partial<Record<TormoBone, { x?: number; y?: number }>> = {
  legL: { x: -1.45 },
  shinL: { x: 1.5 },
  legR: { x: -0.25 },
  shinR: { x: 1.55 },
  body: { x: 0.12 },
  head: { x: 0.1 },
};
/** How far the kneel lowers him (model units). */
export const TORMO_KNEEL_DROP = -0.62;

/**
 * Bakes a pose under the rig: a group between each bone's pivot and its parts, turned as the pose says, so the scenes'
 * clips (which set the pivots) play over the kneel rather than undo it. (The folk's kneel clip on his rig came out with
 * his legs through the floor: his proportions aren't a dwarf's.)
 */
const bakeKneel = (t: TormoBuild, arms: 'tied' | 'rest'): void => {
  const bend = (bone: TormoBone, x: number, y = 0, z = 0): void => {
    const p = t.pivots[bone];
    const kids = [...p.children];
    const gb = new THREE.Group();
    gb.name = `${bone}-bend`;
    gb.rotation.set(x, 0, z);
    gb.position.y = y;
    for (const ch of kids) gb.add(ch);
    p.add(gb);
  };
  for (const [bone, v] of Object.entries(TORMO_KNEEL) as [TormoBone, { x?: number }][]) bend(bone, v.x ?? 0);
  bend('figure', 0, TORMO_KNEEL_DROP);
  if (arms === 'tied') {
    bend('armL', 0.25, 0, 0.12);
    bend('armR', 0.25, 0, -0.12);
  } else {
    // At Nadd's feet: a hand on the raised knee, the other fist on the ground.
    bend('armL', -0.9, 0, 0.1);
    bend('foreL', -0.4);
    bend('armR', 0.35, 0, -0.18);
  }
};

/**
 * The cast's Tormo (`senda:tormo` and its states): himself, the marks on his shoulder, his plates dull. `dark` what got
 * in looking out of him (black eyes); `knelt` on one knee (`tied`: the cable round him, his arms bound).
 */
export const tormoFig = (o?: BaseOpts, st: { dark?: boolean; knelt?: 'tied' | 'rest'; clan?: boolean } = {}): Model => {
  const t = buildTormo({ eyes: st.dark ? 'dark' : 'blue', clan: !!st.clan, plain: true, ...(st.knelt === 'tied' ? { tied: true } : {}) }, o);
  if (st.knelt) bakeKneel(t, st.knelt);
  return { root: t.root, tick: () => undefined, height: Math.round(TORMO_TALL * (st.knelt ? 0.78 : 1) * 100) / 100, rig: t.rig };
};

/** An ash cub: a hunched lump of grey ash on short legs, embers in its cracks, two pale points of eyes. */
export const ashCub = (o?: BaseOpts): Model => {
  const c = begin(o, 0.36, 907);
  const { g } = c;
  const ash = std('#4b4744', { roughness: 1, flatShading: true });
  const ashD = std('#2f2c2a', { roughness: 1, flatShading: true });
  const ember = new THREE.MeshBasicMaterial({ color: '#ff5a32' });
  const eye = new THREE.MeshBasicMaterial({ color: '#ffd9cc' });
  const dode = new THREE.DodecahedronGeometry(0.5, 0);
  const body = M(dode, ash, g);
  body.position.set(0, 0.36, 0);
  body.scale.set(0.5, 0.4, 0.56);
  const hd = M(dode, ashD, g);
  hd.position.set(0, 0.5, 0.24);
  hd.scale.set(0.3, 0.26, 0.28);
  for (const [x, y, z, r] of [
    [0.12, 0.44, 0.2, 0.3],
    [-0.16, 0.36, 0.12, -0.5],
    [0.02, 0.56, -0.12, 1.2],
  ] as const) {
    const crack = M(BOX, ember, g);
    crack.position.set(x, y, z);
    crack.scale.set(0.04, 0.18, 0.04);
    crack.rotation.z = r;
  }
  for (const k of [-1, 1]) {
    const leg = M(dode, ashD, g);
    leg.position.set(k * 0.15, 0.1, 0.05);
    leg.scale.setScalar(0.14);
    const e = M(SPH, eye, g);
    e.position.set(k * 0.08, 0.54, 0.38);
    e.scale.setScalar(0.05);
  }
  const tick = (n: number): void => {
    body.scale.y = 0.4 + (n % 2) * 0.02;
    ember.color.setRGB(1, 0.3 + 0.1 * (n % 3), 0.18);
  };
  return done(c, 0.72, tick);
};

/** The chapter's models by name (null: none of them). */
export const tormoModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'tormo':
      return tormoFig(o);
    case 'tormoDark':
      return tormoFig(o, { dark: true });
    case 'tormoTied':
      return tormoFig(o, { dark: true, knelt: 'tied' });
    case 'tormoKnelt':
      return tormoFig(o, { knelt: 'rest' });
    case 'ashCub':
      return ashCub(o);
    default:
      return null;
  }
};
